import React, { useState, useRef, useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import AkButton from '@atlaskit/button/custom-theme-button';
import GrowHorizontalIcon from '@atlaskit/icon/core/grow-horizontal';
import ShrinkHorizontalIcon from '@atlaskit/icon/core/shrink-horizontal';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import {
	ContentAppearanceType,
	ContentAppearancePropertyKey,
} from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { ContentAppearanceButton } from '@confluence/content-appearance/entry-points/ContentAppearanceButton.ts';
import { updateContentAppearancePropertyWithRetry } from '@confluence/content-appearance/entry-points/contentAppearance.ts';

import type {
	EditContentAppearanceButtonProps,
	UpdateContentAppearancePropertyBody,
} from './editorAppearanceTypes.ts';

const i18n = defineMessages({
	fixedWidth: {
		id: 'editor-appearance-button.toggle.fixed.width',
		defaultMessage: 'Make full-width',
		description: 'tooltip text for when the full width toggle is in fixed width mode',
	},
	fullWidth: {
		id: 'editor-appearance-button.toggle.full.width',
		defaultMessage: 'Make fixed-width',
		description: 'tooltip text for when the full width toggle is in full width mode',
	},
});

export const EditorAppearanceButtonComponent = ({
	contentAppearanceDraft,
	contentId,
	updateContentAppearanceProperty,
	collabEditProvider,
	isDisabled,
	isStatusDisconnected,
	setContentAppearance,
}: EditContentAppearanceButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const apolloClient = getApolloClient();

	const [shouldDisableButton, setShouldDisableButton] = useState<boolean>(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const isFullWidth = contentAppearanceDraft.appearance === ContentAppearanceType.FULL_WIDTH;
	const i18nTooltipTextMessage = isFullWidth ? i18n.fullWidth : i18n.fixedWidth;
	const tooltipText = formatMessage(i18nTooltipTextMessage);

	const triggerAnalyticsEvent = useCallback(
		(eventName: string, extraData = {}) => {
			const analyticDetails = {
				contentId,
				...extraData,
			};

			createAnalyticsEvent!({
				type: 'sendTrackEvent',
				data: {
					action: eventName,
					actionSubject: 'updateContentAppearanceDraft',
					source: 'EditContentAppearanceButtonComponent',
					attributes: analyticDetails,
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	const getToggleWidthParams = useCallback((): UpdateContentAppearancePropertyBody => {
		const newAppearance = isFullWidth
			? ContentAppearanceType.DEFAULT
			: ContentAppearanceType.FULL_WIDTH;

		const newVersion = contentAppearanceDraft.version ? contentAppearanceDraft.version + 1 : 1;

		return {
			contentId: contentId || '',
			propertyKey: ContentAppearancePropertyKey.DRAFT,
			propertyValue: newAppearance,
			versionNumber: newVersion,
		};
	}, [contentId, contentAppearanceDraft, isFullWidth]);

	const toggleWidth = useCallback(async () => {
		const wasFocused = document.activeElement === buttonRef.current;

		if (contentId) {
			// Disable button to prevent clicks on it while mutation is occurring to prevent 409s
			setShouldDisableButton(true);

			let params = getToggleWidthParams();
			params = await updateContentAppearancePropertyWithRetry(
				updateContentAppearanceProperty,
				params,
				apolloClient,
				collabEditProvider,
			);

			setShouldDisableButton(false);

			triggerAnalyticsEvent('content.appearance.draft', {
				appearance: params.propertyValue,
				previousAppearance: contentAppearanceDraft.appearance,
				appearanceVersion: params.versionNumber,
			});

			if (wasFocused) {
				buttonRef.current?.focus();
			}
		}
	}, [
		contentId,
		contentAppearanceDraft,
		apolloClient,
		updateContentAppearanceProperty,
		collabEditProvider,
		getToggleWidthParams,
		triggerAnalyticsEvent,
	]);

	const getButtonIcon = useCallback((): JSX.Element => {
		if (isFullWidth) {
			return <ShrinkHorizontalIcon color="currentColor" spacing="spacious" label={tooltipText} />;
		}
		return <GrowHorizontalIcon color="currentColor" spacing="spacious" label={tooltipText} />;
	}, [isFullWidth, tooltipText]);

	return (
		<ContentAppearanceButton
			userOffline={isStatusDisconnected}
			apolloClient={apolloClient}
			params={getToggleWidthParams()}
			collabEditProvider={collabEditProvider}
			currentContentAppearance={contentAppearanceDraft}
			setContentAppearance={setContentAppearance}
		>
			<AkTooltip content={tooltipText} tag="span" hideTooltipOnClick>
				<AkButton
					ref={buttonRef}
					appearance="subtle"
					iconBefore={getButtonIcon()}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ minWidth: 36 }}
					onClick={toggleWidth}
					isDisabled={
						shouldDisableButton ||
						// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
						(!Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) && isDisabled)
					}
					data-test-id="editor-appearance-button"
				/>
			</AkTooltip>
		</ContentAppearanceButton>
	);
};
